import { useCallback, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Sparkles, Loader2, CheckCircle2, AlertCircle, X } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import {
  useActiveJobsStore,
  type ActiveJob,
  type ActiveJobStatus,
} from '@/stores/active-jobs-store'
import { cancelAgentTurn } from '@/lib/agent-client'
import { toast } from 'sonner'
import { DocumentJobStatusIcon, DocumentJobTrayDetail } from '@/components/DocumentJobProgress'
import { buildSectionLink } from '@/lib/active-job-link'

function isTerminal(status: ActiveJobStatus): boolean {
  return status === 'completed' || status === 'failed' || status === 'cancelled'
}

export function ActiveJobsTray() {
  const navigate = useNavigate()
  const jobs = useActiveJobsStore((s) => s.jobs)
  const dismissJob = useActiveJobsStore((s) => s.dismissJob)
  const fetchActive = useActiveJobsStore((s) => s.fetchActive)
  const cancelDocumentJob = useActiveJobsStore((s) => s.cancelDocumentJob)
  const [cancellingId, setCancellingId] = useState<string | null>(null)

  const { runningCount, waitingCount, visibleJobs } = useMemo(() => {
    // A queued document job is waiting its turn, not running (R1 P3′).
    const waiting = jobs.filter((j) => j.jobType === 'document_job' && j.status === 'pending')
    const running = jobs.filter((j) => !isTerminal(j.status) && !waiting.includes(j))
    return { runningCount: running.length, waitingCount: waiting.length, visibleJobs: jobs }
  }, [jobs])
  const activeCount = runningCount + waitingCount
  const activeLabel = [
    runningCount > 0 ? `${runningCount} running` : null,
    waitingCount > 0 ? `${waitingCount} waiting` : null,
  ].filter(Boolean).join(' · ')

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (open) void fetchActive()
    },
    [fetchActive]
  )

  const handleOpen = useCallback(
    (job: ActiveJob) => {
      const link = buildSectionLink(job)
      if (link) void navigate(link)
    },
    [navigate]
  )

  const handleCancel = useCallback(
    async (job: ActiveJob) => {
      setCancellingId(job.jobId)
      try {
        if (job.jobType === 'document_job') {
          // A document job may have no session: it is cancelled by id (cancel_document_job).
          const result = await cancelDocumentJob(job.jobId)
          if (!result.ok) toast.error(result.message)
          return
        }
        if (!job.sessionId) return
        await cancelAgentTurn({ sessionId: job.sessionId })
        void fetchActive()
      } finally {
        setCancellingId(null)
      }
    },
    [fetchActive, cancelDocumentJob]
  )

  const handleNavigate = useCallback((href: string) => void navigate(href), [navigate])

  if (visibleJobs.length === 0) return null

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="sm" className="relative h-8 gap-2 px-2">
          {runningCount > 0 ? (
            <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--ai-accent))]" />
          ) : (
            <Sparkles className="h-4 w-4 text-muted-foreground" />
          )}
          <span className="text-xs">
            {activeCount > 0 ? activeLabel : 'AI tasks'}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <h3 className="text-sm font-medium">Active AI tasks</h3>
          <span className="text-meta">
            {activeCount > 0 ? activeLabel : '0 running'}
          </span>
        </div>
        <div className="max-h-96 overflow-y-auto">
          {visibleJobs.map((job) => (
            <JobRow
              key={job.jobId}
              job={job}
              cancelling={cancellingId === job.jobId}
              onOpen={() => handleOpen(job)}
              onCancel={() => void handleCancel(job)}
              onDismiss={() => dismissJob(job.jobId)}
              onNavigate={handleNavigate}
            />
          ))}
        </div>
      </PopoverContent>
    </Popover>
  )
}

interface JobRowProps {
  job: ActiveJob
  cancelling: boolean
  onOpen: () => void
  onCancel: () => void
  onDismiss: () => void
  onNavigate: (href: string) => void
}

function JobRow({ job, cancelling, onOpen, onCancel, onDismiss, onNavigate }: JobRowProps) {
  const terminal = isTerminal(job.status)
  const isDocumentJob = job.jobType === 'document_job'
  const statusIcon = isDocumentJob ? <DocumentJobStatusIcon job={job} /> : getStatusIcon(job.status)
  const canOpen = Boolean(job.bidId)

  return (
    <div className="flex items-start gap-3 border-b px-4 py-3 last:border-b-0">
      <div className="mt-0.5 flex-shrink-0">{statusIcon}</div>
      <div className="flex-1 min-w-0">
        <button
          onClick={onOpen}
          disabled={!canOpen}
          className={cn(
            'text-left text-xs font-medium truncate block w-full',
            canOpen && 'hover:underline'
          )}
        >
          {job.title ?? 'AI task'}
        </button>
        {isDocumentJob && <DocumentJobTrayDetail job={job} onNavigate={onNavigate} />}
        {!isDocumentJob && job.progressMessage && !terminal && (
          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
            {job.progressMessage}
          </p>
        )}
        {!isDocumentJob && job.status === 'failed' && job.errorMessage && (
          <p className="text-[11px] text-destructive mt-0.5 truncate">
            {job.errorMessage}
          </p>
        )}
        {!isDocumentJob && !terminal && (
          <div className="mt-1.5 h-1 w-full overflow-hidden rounded-full bg-muted">
            <div
              className="h-full rounded-full bg-[hsl(var(--ai-accent))] transition-all"
              style={{ width: `${Math.max(2, job.progress)}%` }}
            />
          </div>
        )}
        <p className="text-[11px] text-muted-foreground mt-1">
          {formatDistanceToNow(new Date(job.createdAt), { addSuffix: true })}
        </p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-1">
        {terminal ? (
          <button
            onClick={onDismiss}
            aria-label="Dismiss"
            className="rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        ) : (
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-[11px]"
            disabled={cancelling}
            onClick={onCancel}
          >
            {cancelling ? 'Cancelling…' : 'Cancel'}
          </Button>
        )}
      </div>
    </div>
  )
}

function getStatusIcon(status: ActiveJobStatus) {
  if (status === 'completed') {
    return <CheckCircle2 className="h-4 w-4 text-emerald-600" />
  }
  if (status === 'failed') {
    return <AlertCircle className="h-4 w-4 text-destructive" />
  }
  if (status === 'cancelled') {
    return <X className="h-4 w-4 text-muted-foreground" />
  }
  return <Loader2 className="h-4 w-4 animate-spin text-[hsl(var(--ai-accent))]" />
}
